import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DeviceMobile, Share, Plus, DotsThreeVertical, AppleLogo, AndroidLogo, CheckCircle } from "@phosphor-icons/react";

/**
 * InstallGuideModal — explicit "How to install MedEasy on your phone"
 *
 * Trigger this from the landing header so users who dismissed the auto-banner
 * (or who are browsing on desktop) can always find install instructions.
 * Shows side-by-side instructions for iPhone (Safari) and Android (Chrome).
 */
export default function InstallGuideModal({ trigger }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="install-guide-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E3E1D9]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#F7F6F2] ring-1 ring-[#E3E1D9] flex items-center justify-center overflow-hidden">
              <img src="/icon-192.png" alt="MedEasy" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <DialogTitle className="text-xl">Install MedEasy on your phone</DialogTitle>
              <p className="text-xs text-[#5C6660] mt-0.5">No App Store needed. Lives on your home screen like a native app.</p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* iPhone */}
          <section className="rounded-2xl border border-[#E3E1D9] bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <AppleLogo size={20} weight="fill" className="text-[#1E2420]" />
              <h3 className="font-[Manrope] font-semibold text-base">On iPhone (Safari only)</h3>
            </div>
            <ol className="space-y-2.5 text-sm text-[#3D4540] leading-relaxed">
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">1</span>
                <span>Open <strong>Safari</strong> (not Chrome) and visit <strong>mymedeasy.ca</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">2</span>
                <span>Tap the <Share size={14} weight="bold" className="inline -mt-0.5" /> <strong>Share</strong> button at the bottom of the screen</span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">3</span>
                <span>Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong> <Plus size={12} weight="bold" className="inline -mt-0.5" /></span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">4</span>
                <span>Tap <strong>&ldquo;Add&rdquo;</strong> in the top right</span>
              </li>
            </ol>
          </section>

          {/* Android */}
          <section className="rounded-2xl border border-[#E3E1D9] bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <AndroidLogo size={20} weight="fill" className="text-[#3DDC84]" />
              <h3 className="font-[Manrope] font-semibold text-base">On Android (Chrome)</h3>
            </div>
            <ol className="space-y-2.5 text-sm text-[#3D4540] leading-relaxed">
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">1</span>
                <span>Open <strong>Chrome</strong> and visit <strong>mymedeasy.ca</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">2</span>
                <span>Tap the <DotsThreeVertical size={14} weight="bold" className="inline -mt-0.5" /> <strong>menu</strong> in the top-right corner</span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">3</span>
                <span>Tap <strong>&ldquo;Install app&rdquo;</strong> or <strong>&ldquo;Add to Home screen&rdquo;</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#2C4C3B] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">4</span>
                <span>Tap <strong>&ldquo;Install&rdquo;</strong> to confirm</span>
              </li>
            </ol>
          </section>

          <div className="flex items-start gap-2 text-xs text-[#5C6660] bg-[#F7F6F2] rounded-xl p-3">
            <CheckCircle size={16} weight="fill" className="text-[#2C4C3B] shrink-0 mt-0.5" />
            <div>
              Once installed, MedEasy opens full-screen like a native app — no browser bars, faster start, and your data stays exactly where you left it.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { DeviceMobile };
